import React, { useState, useEffect } from 'react';
import { Heart, Target, Globe, Banknote, Sparkles } from 'lucide-react';

const IkigaiDiagram = ({ analysis, language = 'fr', showLabels = true, size = 'large' }) => {
  const [animated, setAnimated] = useState(false);
  const [hoveredCircle, setHoveredCircle] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setAnimated(true), 300);
    return () => clearTimeout(timer);
  }, []);

  const sizeClasses = {
    small: 'max-w-[300px]',
    medium: 'max-w-[400px]',
    large: 'max-w-[500px]'
  };

  const circles = [
    { 
      id: 'passions', 
      color: '#FF6B6B', 
      label: language === 'fr' ? 'Ce que tu AIMES' : 'What you LOVE',
      shortLabel: 'Passions',
      icon: Heart,
      items: Array.isArray(analysis?.core_passions) ? analysis.core_passions : [],
      position: { top: '0%', left: '25%' }
    },
    { 
      id: 'talents', 
      color: '#FFE66D', 
      label: language === 'fr' ? 'Ce pour quoi tu es DOUÉ' : 'What you are GOOD AT',
      shortLabel: 'Talents',
      icon: Target,
      items: Array.isArray(analysis?.natural_talents) ? analysis.natural_talents : [],
      position: { top: '25%', left: '50%' }
    },
    { 
      id: 'utility', 
      color: '#4ECDC4', 
      label: language === 'fr' ? 'Ce dont le monde a BESOIN' : 'What the world NEEDS',
      shortLabel: language === 'fr' ? 'Utilité' : 'Utility',
      icon: Globe,
      items: Array.isArray(analysis?.causes) ? analysis.causes : [],
      position: { top: '50%', left: '25%' }
    },
    { 
      id: 'monetization', 
      color: '#1A535C', 
      label: language === 'fr' ? 'Ce pour quoi tu peux être PAYÉ' : 'What you can be PAID FOR',
      shortLabel: language === 'fr' ? 'Métier' : 'Career',
      icon: Banknote,
      items: Array.isArray(analysis?.career_paths) ? analysis.career_paths.map(c => typeof c === 'string' ? c : c?.title || c?.name || '') : [],
      position: { top: '25%', left: '0%' }
    }
  ];

  const intersections = [
    { 
      id: 'passion-mission', 
      label: language === 'fr' ? 'Mission' : 'Mission',
      position: { top: '12%', left: '37%' },
      colors: ['#FF6B6B', '#4ECDC4']
    },
    { 
      id: 'passion-profession', 
      label: language === 'fr' ? 'Passion' : 'Passion',
      position: { top: '37%', left: '50%' },
      colors: ['#FF6B6B', '#FFE66D']
    },
    { 
      id: 'profession-vocation', 
      label: language === 'fr' ? 'Profession' : 'Profession',
      position: { top: '50%', left: '37%' },
      colors: ['#FFE66D', '#1A535C']
    },
    { 
      id: 'vocation-mission', 
      label: language === 'fr' ? 'Vocation' : 'Vocation',
      position: { top: '37%', left: '12%' },
      colors: ['#4ECDC4', '#1A535C']
    }
  ];

  return (
    <div className={`relative ${sizeClasses[size]} w-full aspect-square mx-auto`}>
      {/* Main circles with overlap effect */}
      <svg viewBox="0 0 200 200" className="w-full h-full">
        <defs>
          {circles.map((circle) => (
            <radialGradient key={`grad-${circle.id}`} id={`grad-${circle.id}`}>
              <stop offset="0%" stopColor={circle.color} stopOpacity="0.3" />
              <stop offset="70%" stopColor={circle.color} stopOpacity="0.15" />
              <stop offset="100%" stopColor={circle.color} stopOpacity="0.05" />
            </radialGradient>
          ))}
          <radialGradient id="ikigai-center">
            <stop offset="0%" stopColor="#FF6B6B" stopOpacity="1" />
            <stop offset="50%" stopColor="#FFE66D" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#4ECDC4" stopOpacity="0.8" />
          </radialGradient>
        </defs>

        {/* Circle backgrounds with animation */}
        {circles.map((circle, index) => {
          const positions = [
            { cx: 70, cy: 60 },   // passions (top-left)
            { cx: 130, cy: 60 },  // talents (top-right)
            { cx: 130, cy: 140 }, // utility (bottom-right)
            { cx: 70, cy: 140 }   // monetization (bottom-left)
          ];
          const pos = positions[index];
          const isHovered = hoveredCircle === circle.id;
          
          return (
            <g key={circle.id}>
              {/* Outer glow on hover */}
              {isHovered && (
                <circle
                  cx={pos.cx}
                  cy={pos.cy}
                  r="58"
                  fill="none"
                  stroke={circle.color}
                  strokeWidth="2"
                  opacity="0.5"
                  className="animate-pulse"
                />
              )}
              {/* Main circle */}
              <circle
                cx={pos.cx}
                cy={pos.cy}
                r={animated ? 55 : 0}
                fill={`url(#grad-${circle.id})`}
                stroke={circle.color}
                strokeWidth={isHovered ? "3" : "2"}
                opacity={animated ? 1 : 0}
                style={{
                  transition: `all 0.8s ease-out ${index * 0.15}s`,
                  cursor: 'pointer'
                }}
                onMouseEnter={() => setHoveredCircle(circle.id)}
                onMouseLeave={() => setHoveredCircle(null)}
              />
            </g>
          );
        })}

        {/* Center Ikigai circle */}
        <circle
          cx="100"
          cy="100"
          r={animated ? 25 : 0}
          fill="url(#ikigai-center)"
          style={{
            transition: 'all 1s ease-out 0.6s',
            filter: 'drop-shadow(0 4px 12px rgba(255, 107, 107, 0.4))'
          }}
        />
      </svg>

      {/* Circle labels with icons */}
      {showLabels && circles.map((circle, index) => {
        const Icon = circle.icon;
        const labelPositions = [
          { top: '2%', left: '15%' },
          { top: '2%', right: '15%' },
          { bottom: '2%', right: '15%' },
          { bottom: '2%', left: '15%' }
        ];
        const pos = labelPositions[index];
        
        return (
          <div
            key={`label-${circle.id}`}
            className={`absolute flex items-center gap-1.5 px-2 py-1 rounded-full transition-all duration-500 cursor-pointer ${
              hoveredCircle === circle.id 
                ? 'bg-white shadow-lg scale-110' 
                : 'bg-white/80 hover:bg-white'
            } ${animated ? 'opacity-100' : 'opacity-0'}`}
            style={{
              ...pos,
              transitionDelay: `${0.8 + index * 0.1}s`,
              border: `2px solid ${circle.color}`
            }}
            onMouseEnter={() => setHoveredCircle(circle.id)}
            onMouseLeave={() => setHoveredCircle(null)}
          >
            <Icon className="w-3.5 h-3.5" style={{ color: circle.color }} />
            <span className="text-xs font-semibold" style={{ color: circle.color }}>
              {circle.shortLabel}
            </span>
          </div>
        );
      })}

      {/* Center Ikigai label */}
      <div
        className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center justify-center transition-all duration-700 ${
          animated ? 'opacity-100 scale-100' : 'opacity-0 scale-50'
        }`}
        style={{ transitionDelay: '1s' }}
      >
        <Sparkles className="w-6 h-6 text-white mb-1" />
        <span className="font-serif text-sm font-bold text-white">IKIGAI</span>
      </div>

      {/* Intersection labels */}
      {showLabels && intersections.map((inter, index) => (
        <div
          key={inter.id}
          className={`absolute text-[10px] font-medium text-stone-500 bg-white/90 px-1.5 py-0.5 rounded transition-all duration-500 ${
            animated ? 'opacity-70' : 'opacity-0'
          }`}
          style={{
            ...inter.position,
            transitionDelay: `${1.2 + index * 0.1}s`
          }}
        >
          {inter.label}
        </div>
      ))}

      {/* Hover tooltip */}
      {hoveredCircle && (
        <div 
          className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full mt-4 bg-white rounded-xl shadow-xl p-4 w-64 z-10 border border-stone-100"
          style={{ animation: 'fadeIn 0.2s ease-out' }}
        >
          {circles.filter(c => c.id === hoveredCircle).map(circle => {
            const Icon = circle.icon;
            return (
              <div key={circle.id}>
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="w-4 h-4" style={{ color: circle.color }} />
                  <span className="font-semibold text-stone-800">{circle.label}</span>
                </div>
                {circle.items.length > 0 && (
                  <ul className="space-y-1">
                    {circle.items.slice(0, 3).map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-stone-600">
                        <div 
                          className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" 
                          style={{ backgroundColor: circle.color }}
                        />
                        <span className="line-clamp-1">{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translate(-50%, calc(100% + 10px)); }
          to { opacity: 1; transform: translate(-50%, calc(100% + 16px)); }
        }
      `}</style>
    </div>
  );
};

export default IkigaiDiagram;
